import React, { createContext, useContext, useEffect, useCallback } from 'react';
import { companyAPI } from '../services/api';
import { useAuth } from './AuthContext';

/**
 * Default brand palette — Deep Navy + Professional Blue + Emerald.
 */
const DEFAULTS = {
  primary:    '#0F2747',
  accent:     '#2563EB',
  success:    '#16A34A',
  warning:    '#D97706',
  danger:     '#DC2626',
  // Per-document overrides (null = use accent)
  invoice_color:    null,
  estimate_color:   null,
  sales_order_color:null,
  purchase_order_color: null,
  sales_invoice_color:  null,
  purchase_invoice_color: null,
  // Border styles: solid, dashed, double, thick, dotted, none
  invoice_border:     'none',
  estimate_border:    'none',
  sales_order_border: 'none',
  purchase_order_border: 'none',
  purchase_invoice_border: 'none',
};

const BrandContext = createContext(null);

export function BrandProvider({ children }) {  const apply = useCallback((branding) => {
    const b = { ...DEFAULTS, ...branding };
    const r = document.documentElement.style;
    // Core palette
    r.setProperty('--brand-primary',       b.primary);
    r.setProperty('--brand-accent',        b.accent);
    r.setProperty('--brand-accent-light',  hexToRGBA(b.accent, 0.08));
    r.setProperty('--brand-success',       b.success);
    r.setProperty('--brand-warning',       b.warning);
    r.setProperty('--brand-danger',        b.danger);
    r.setProperty('--brand-document',      b.invoice_color || b.accent);
    // Desktop chrome — derive from brand palette
    r.setProperty('--desk-dark',           b.primary);
    r.setProperty('--desk-dark-2',         darken(b.primary, 20));
    r.setProperty('--desk-toolbar-bg',     b.primary);
    r.setProperty('--desk-accent',         b.accent);
    r.setProperty('--desk-accent-dark',    darken(b.accent, 12));
    r.setProperty('--desk-accent-soft',    hexToRGBA(b.accent, 0.08));
    r.setProperty('--desk-green',          b.success);
    r.setProperty('--desk-green-bg',       hexToRGBA(b.success, 0.10));
    r.setProperty('--desk-amber',          b.warning);
    r.setProperty('--desk-amber-bg',       hexToRGBA(b.warning, 0.10));
    r.setProperty('--desk-red',            b.danger);
    r.setProperty('--desk-red-bg',         hexToRGBA(b.danger, 0.10));
    // Derive sidebar hover from primary
    r.setProperty('--bg-sidebar-hover',    lighten(b.primary, 12));
    r.setProperty('--bg-sidebar-active',   b.accent);
    // Generic brand variables used by the base CSS
    r.setProperty('--primary',             b.accent);
    r.setProperty('--primary-dark',        darken(b.accent, 12));
    r.setProperty('--primary-light',       hexToRGBA(b.accent, 0.08));
    r.setProperty('--success',             b.success);
    r.setProperty('--success-light',       hexToRGBA(b.success, 0.10));
    r.setProperty('--warning',             b.warning);
    r.setProperty('--warning-light',       hexToRGBA(b.warning, 0.10));
    r.setProperty('--danger',              b.danger);
    r.setProperty('--danger-light',        hexToRGBA(b.danger, 0.10));
    r.setProperty('--brand-accent-light',  hexToRGBA(b.accent, 0.08));
    // Document-specific colors stored as CSS variables for targeted use
    r.setProperty('--doc-invoice',         b.invoice_color || b.accent);
    r.setProperty('--doc-estimate',        b.estimate_color || b.accent);
    r.setProperty('--doc-sales-order',     b.sales_order_color || b.accent);
    r.setProperty('--doc-purchase-order',  b.purchase_order_color || b.accent);
    r.setProperty('--doc-sales-invoice',   b.sales_invoice_color  || b.accent);
    r.setProperty('--doc-purchase-invoice',b.purchase_invoice_color || b.accent);
    // Document-specific border styles — full border-bottom value
    const accent = b.accent || '#2563EB';
    r.setProperty('--doc-invoice-border',     getBorderCSS(b.invoice_border, b.invoice_color || accent));
    r.setProperty('--doc-estimate-border',    getBorderCSS(b.estimate_border, b.estimate_color || accent));
    r.setProperty('--doc-sales-order-border', getBorderCSS(b.sales_order_border, b.sales_order_color || accent));
    r.setProperty('--doc-purchase-order-border', getBorderCSS(b.purchase_order_border, b.purchase_order_color || accent));
    r.setProperty('--doc-purchase-invoice-border', getBorderCSS(b.purchase_invoice_border, b.purchase_invoice_color || accent));
  }, []);

  const { user } = useAuth();

  useEffect(() => {
    // Try to load saved branding from localStorage (cached from last login)
    try {
      const cached = localStorage.getItem('ledgerpro_branding');
      if (cached) apply(JSON.parse(cached));
    } catch (e) { /* ignore */ }

    // Only fetch fresh branding from API if user is authenticated
    if (!user) return;
    companyAPI.current()
      .then((company) => {
        if (!company) return;
        const branding = {
          primary:              company.brand_primary     || DEFAULTS.primary,
          accent:               company.brand_accent      || DEFAULTS.accent,
          success:              company.brand_success     || DEFAULTS.success,
          warning:              company.brand_warning     || DEFAULTS.warning,
          danger:               company.brand_danger      || DEFAULTS.danger,
          invoice_color:        company.invoice_color     || null,
          estimate_color:       company.estimate_color    || null,
          sales_order_color:    company.sales_order_color || null,
          purchase_order_color: company.purchase_order_color || null,
          sales_invoice_color:  company.sales_invoice_color  || null,
          purchase_invoice_color: company.purchase_invoice_color || null,
        };
        localStorage.setItem('ledgerpro_branding', JSON.stringify(branding));
        apply(branding);
      })
      .catch(() => { /* keep defaults or cached */ });
  }, [apply]);

  return (
    <BrandContext.Provider value={{ apply }}>
      {children}
    </BrandContext.Provider>
  );
}

export const useBrand = () => useContext(BrandContext);

/* ---------- helpers ---------- */

function hexToRGBA(hex, alpha) {
  if (!hex || !hex.startsWith('#')) return `rgba(37,99,235,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function lighten(hex, amount) {
  if (!hex || !hex.startsWith('#')) return '#1a3a5c';
  const r = Math.min(255, parseInt(hex.slice(1, 3), 16) + amount);
  const g = Math.min(255, parseInt(hex.slice(3, 5), 16) + amount);
  const b = Math.min(255, parseInt(hex.slice(5, 7), 16) + amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function getBorderCSS(style, color) {
  const c = color || '#2563EB';
  switch (style) {
    case 'solid':  return `2px solid ${c}`;
    case 'dashed': return `2px dashed ${c}`;
    case 'double': return `3px double ${c}`;
    case 'thick':  return `3px solid ${c}`;
    case 'dotted': return `2px dotted ${c}`;
    case 'none':
    default:       return 'none';
  }
}

function darken(hex, amount) {
  if (!hex || !hex.startsWith('#')) return '#0a1525';
  const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - amount);
  const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - amount);
  const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
